import { MagnifyingGlassMini, XMarkMini } from '@medusajs/icons';
import { FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { DEFAULT_COUNTRY_CODE } from '@lib/constants/country-code';
import Button from '@modules/common/components/button';
import { useMobileSearch } from '@modules/layout/components/mobile-search';

import SearchBoxWrapper, {
  ControlledSearchBoxProps,
} from '../search-box-wrapper';

const ControlledSearchBox = ({
  inputRef,
  onChange,
  onReset,
  placeholder,
  value,
  ...props
}: ControlledSearchBoxProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { countryCode } = useParams<{ countryCode: string }>();
  const code = countryCode || DEFAULT_COUNTRY_CODE;

  // Try to get mobile search context, but don't fail if not available
  let toggleSearch: (() => void) | undefined;
  let isOpen = false;
  try {
    const mobileSearch = useMobileSearch();
    toggleSearch = mobileSearch.toggleDrawer;
    isOpen = mobileSearch.isOpen;
  } catch {
    // Mobile search context not available - that's okay for desktop
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (inputRef.current?.value) {
      const params = new URLSearchParams(searchParams);
      params.set('q', value);
      navigate(`/${code}/search?${params.toString()}`);
    }
    if (inputRef.current) {
      inputRef.current.blur();
    }
    if (isOpen && toggleSearch) {
      toggleSearch();
    }
  };

  const handleReset = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onReset(event);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div
      {...props}
      className="bg-white-30 text-ui-fg-on-color md:rounded-rounded flex w-full items-center gap-x-2 rounded-full px-4 py-2 backdrop-blur-2xl"
    >
      <MagnifyingGlassMini className="text-grey-60" />
      <form
        action=""
        className="w-full"
        noValidate
        onSubmit={handleSubmit}
        onReset={handleReset}
      >
        <div className="flex items-center justify-between">
          <input
            ref={inputRef}
            data-testid="search-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            placeholder={placeholder}
            spellCheck={false}
            type="search"
            value={value}
            onChange={onChange}
            className="txt-compact-large text-black-50 placeholder:text-grey-60 h-6 flex-1 bg-transparent placeholder:transition-colors focus:outline-none"
          />
          {value && (
            <button
              onClick={handleReset}
              type="button"
              className="txt-compact-large text-grey-60 flex items-center justify-center gap-x-2 px-2"
              data-testid="search-clear-button"
            >
              <XMarkMini />
            </button>
          )}
          <Button
            className="hidden md:block"
            type="submit"
            disabled={!value}
            data-testid="search-submit-button"
          >
            搜尋
          </Button>
        </div>
      </form>
    </div>
  );
};

const SearchBox = (props: { placeholder?: string }) => {
  return (
    <SearchBoxWrapper placeholder={props.placeholder || ''}>
      {(state) => {
        return <ControlledSearchBox {...state} />;
      }}
    </SearchBoxWrapper>
  );
};

export default SearchBox;
